import React, { useEffect, useState, useRef } from 'react';
import { Link } from "react-router-dom";
import Select from "react-select";
import { Button } from "react-bootstrap";
import swal from "sweetalert2";
import bgimage from '../../../../images/generate/cooker.gif';

const ManageItemQuantity = () => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true); 
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [generateClicked, setGenerateClicked] = useState(false);
    const [masterChecked, setMasterChecked] = useState(false);
    const [itemStates, setItemStates] = useState({});
    const [showValidation, setShowValidation] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [isEditMode, setIsEditMode] = useState(false); // NEW

    const scrollRefs = useRef([]);

    useEffect(() => {
        fetch(`${baseURL}/products/grouped-by-product-item-category`)
            .then(res => {
                if (!res.ok) throw new Error('Network response was not ok');
                return res.json();
            })
            .then(json => {
                setData(json.data || []);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const productCategoryOptions = data.map(category => ({
        value: category.product_category_id.toString(),
        label: category.product_category_name,
    }));

    const productOptions = selectedCategory
        ? data.find(cat => cat.product_category_id.toString() === selectedCategory)?.products.map(prod => ({
            value: prod.product_id.toString(),
            label: prod.product_name,
        })) || []
        : [];

    const selectedProductObj = data
        .find(cat => cat.product_category_id.toString() === selectedCategory)
        ?.products.find(prod => prod.product_id.toString() === selectedProduct);

    const handleCategoryChange = (selectedOption) => {
        setSelectedCategory(selectedOption.value);
        setSelectedProduct(null);
        setGenerateClicked(false);
        setItemStates({});
        setShowValidation(false);
        setIsEditMode(false);
    };

    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption.value);
        setGenerateClicked(false);
        setItemStates({});
        setShowValidation(false);
        setIsEditMode(false);
    };

    const handleMasterCheckboxChange = () => {
        const newChecked = !masterChecked;
        setMasterChecked(newChecked);
        const updatedStates = {};
        selectedProductObj?.item_categories.forEach(category => {
            category.items.forEach(item => {
                updatedStates[item.item_id] = {
                    checked: newChecked,
                    quantity: 1
                };
            });
        });
        setItemStates(updatedStates);
    };

    const handleItemCheckboxChange = (itemId) => {
        setItemStates(prev => {
            const current = prev[itemId] || { quantity: 1 };
            const newChecked = !current.checked;
            return {
                ...prev,
                [itemId]: {
                    checked: newChecked,
                    quantity: newChecked ? (current.quantity || 1) : 1
                }
            };
        });
    };

    const handleQuantityChange = (itemId, value) => {
        let qty = Number(value);
        if (isNaN(qty) || qty < 0) qty = 0;
        setItemStates(prev => ({
            ...prev,
            [itemId]: {
                ...(prev[itemId] || {}),
                quantity: qty,
            }
        }));
    };

    const onReset = () => {
        setSelectedCategory(null);
        setSelectedProduct(null);
        setGenerateClicked(false);
        setMasterChecked(false);
        setItemStates({});
        setShowValidation(false);
        setIsEditMode(false);
    };

    const onGenerate = async () => {
        if (!selectedCategory || !selectedProduct) {
            setShowValidation(true);
            return;
        }

        setShowValidation(false);
        setGenerateClicked(true);
        setMasterChecked(false);
        setIsEditMode(false);

        try {
            const res = await fetch(`${baseURL}/productitemquantity/${selectedProduct}`);
            const result = await res.json();
            const existingItems = result?.data?.item_quantity || [];

            const newItemStates = {};
            selectedProductObj?.item_categories.forEach(category => {
                category.items.forEach(item => {
                    newItemStates[item.item_id] = {
                        checked: false,
                        quantity: 1
                    };
                });
            });

            let hasExisting = false;
            existingItems.forEach(item => {
                newItemStates[item.item_id] = {
                    checked: true,
                    quantity: item.quantity
                };
                hasExisting = true;
            });

            setItemStates(newItemStates);
            setIsEditMode(hasExisting); // SET EDIT MODE BASED ON DATA FOUND

            const totalItems = selectedProductObj?.item_categories.reduce((sum, cat) => sum + cat.items.length, 0) || 0;
            const checkedItems = Object.values(newItemStates).filter(i => i.checked).length;
            setMasterChecked(checkedItems === totalItems);
        } catch (err) {
            console.error("Fetch error:", err);
            setItemStates({});
            setMasterChecked(false);
            setIsEditMode(false);
        }
    };

    const handleSubmit = async () => {
        const payload = {
            product_id: Number(selectedProduct),
            item_quantity: Object.entries(itemStates)
                .filter(([_, val]) => val.checked)
                .map(([item_id, val]) => ({
                    item_id: Number(item_id),
                    quantity: val.quantity
                }))
        };

        if (payload.item_quantity.length === 0) {
            swal.fire("No items selected", "Please select at least one item.", "warning");
            return;
        }

        try {
            const response = await fetch(`${baseURL}/productitemquantity`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (response.ok) {
                swal.fire("Success", result.message, "success").then(() => {
                    window.location.href = "./manage-item-quantity";
                });
            } else {
                swal.fire("Error", result.message || "Something went wrong", "error");
            }
        } catch (error) {
            swal.fire("Error", error.message, "error");
        }
    };

    if (loading) return <p>Loading products...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <>
            {/* Title */}
            <div className="d-flex justify-content-between page-titles px-0 py-1 mx-0">
                <div className='mb-0'>
                    <h4 className='breadcrumb-header'>Manage Product Quantity</h4>
                    <ol className="breadcrumb fs-7">
                        <li className="breadcrumb-item"><Link to='/dashboard'>Dashboard</Link></li>
                        <li className="breadcrumb-item"><i className="fa-solid fa-angles-right fs-8"></i></li>
                        <li className="breadcrumb-item active">Production Management</li>
                    </ol>
                </div>
            </div>

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-lg-3">
                    <label className="text-black font-w500 fs-7">Product Category<span className="required">*</span></label>
                    <Select
                        placeholder="Select Product Category"
                        options={productCategoryOptions}
                        onChange={handleCategoryChange}
                        value={productCategoryOptions.find(opt => opt.value === selectedCategory) || null}
                    />
                    {showValidation && !selectedCategory && <span className="text-danger fs-7">This Field is required</span>}
                </div>

                <div className="col-lg-3">
                    <label className="text-black font-w500 fs-7">Product<span className="required">*</span></label>
                    <Select
                        placeholder="Select Product"
                        options={productOptions}
                        onChange={handleProductChange}
                        value={productOptions.find(opt => opt.value === selectedProduct) || null}
                        isDisabled={!selectedCategory}
                    />
                    {showValidation && !selectedProduct && <span className="text-danger fs-7">This Field is required</span>}
                </div>

                <div className="col-lg-4 mt-4 d-flex gap-2">
                    <Button variant="secondary" size="sm" onClick={onReset}>Reset</Button>
                    <Button variant="primary" size="sm" onClick={onGenerate}>Apply</Button>
                </div>
            </div>

            {/* Table Section */}
            {generateClicked && selectedProductObj && (
                <>
                    <div className="row mx-2">
                        <div className="col-lg-12 mt-3 mb-3 d-flex align-items-center">
                            <input
                                type="checkbox"
                                checked={masterChecked}
                                onChange={handleMasterCheckboxChange}
                                className="form-check-input me-3"
                            />
                            <h5 className="text-danger fw-bold mb-0">
                               Deliverables Product Quantity for {selectedProductObj.product_name}
                            </h5>
                        </div>
                    </div>

                    {selectedProductObj.item_categories
                        .reduce((rows, category, index) => {
                            if (index % 2 === 0) rows.push([category]);
                            else rows[rows.length - 1].push(category);
                            return rows;
                        }, [])
                        .map((pair, rowIndex) => (
                            <div className="row mb-4" key={`row-${rowIndex}`}>
                                {pair.map((category, colIndex) => {
                                    const refIndex = rowIndex * 2 + colIndex;
                                    return (
                                        <div className="col-lg-6 mb-3" key={category.item_category_id}>
                                            <div className="p-3 rounded shadow-sm border bg-white h-100 d-flex flex-column position-relative">
                                                <label className="text-primary fw-semibold fs-5 mb-2">{category.item_category_name}</label>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light position-absolute top-0 end-0 mt-2 me-2"
                                                    onClick={() => setExpanded(prev => ({ ...prev, [refIndex]: !prev[refIndex] }))}
                                                    style={{ zIndex: 3 }}
                                                >
                                                    <i className={`fas ${expanded[refIndex] ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                </button>

                                                <div
                                                    className="table-container position-relative"
                                                    style={{
                                                        maxHeight: expanded[refIndex] ? 'none' : '250px',
                                                        overflowY: expanded[refIndex] ? 'visible' : 'auto',
                                                        transition: 'max-height 0.3s ease'
                                                    }}
                                                    ref={(el) => (scrollRefs.current[refIndex] = el)}
                                                >
                                                    <table className="table align-top table-row-dashed table-striped table-hover fs-7 gy-1 gs-2 dataTable mb-0">
                                                        <thead>
                                                            <tr className="text-start text-muted fw-bold fs-6 gs-0 th_head">
                                                                <th style={{ width: '5%' }}></th>
                                                                <th>Item</th>
                                                                <th style={{ width: '30%' }}>Quantity</th>
                                                                <th style={{ width: '20%' }}>Unit</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {category.items.length > 0 ? category.items.map((item) => {
                                                                const itemState = itemStates[item.item_id] || { checked: false, quantity: 1 };
                                                                const isChecked = itemState.checked;
                                                                return (
                                                                    <tr key={item.item_id}>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isChecked}
                                                                                onChange={() => handleItemCheckboxChange(item.item_id)}
                                                                                className="form-check-input"
                                                                            />
                                                                        </td>
                                                                        <td className="align-middle">{item.item_name}</td>
                                                                        <td className="text-center align-middle">
                                                                            <input
                                                                                type="number"
                                                                                className="form-control form-control-sm text-center fw-bold"
                                                                                value={isChecked ? itemState.quantity : ''}
                                                                                disabled={!isChecked}
                                                                                min={0}
                                                                                style={{
                                                                                    fontSize: '1rem',
                                                                                    fontWeight: '600',
                                                                                    backgroundColor: isChecked ? '#fff' : '#f5f5f5',
                                                                                    borderColor: isChecked ? '#000' : '#ddd',
                                                                                }}
                                                                                onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center align-middle">
                                                                            <span className="fw-semibold text-dark">
                                                                                {item.measurement_quantity_slug_name}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }) : (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center text-muted fst-italic">
                                                                        No items in this category
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                    {/* Submit Button */}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button className="btn-sm px-4 fw-bold" variant="primary" onClick={handleSubmit}>
                            {isEditMode ? 'Update' : 'Create'} <i className="fas fa-angle-double-right ms-2"></i>
                        </Button>
                    </div>
                </>
            )}
        </>
    );
};

export default ManageItemQuantity;
